import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, SegmentedControl, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { Modal, Field, Select, Textarea, TextInput, FormActions } from '../components/form';
import { LeadForm, type LeadRow } from '../components/CrmForms';
import { fmtDate } from '../lib/fmt';

// Reference lookups carry a 5-lang name_i18n; render the active locale, fall back to name.
type I18nName = { name: string; name_i18n?: Record<string, string> | null };
const loc = (o: I18nName | null | undefined, lng: string): string => o ? (o.name_i18n?.[lng] || o.name) : '—';

type Stage = { id: string; name: string; name_i18n?: Record<string, string> | null; position: number; is_won: boolean; is_lost: boolean };
type Lead = LeadRow & {
  created_at: string;
  pipeline_stages: { name: string; name_i18n?: Record<string, string> | null; is_won: boolean; is_lost: boolean } | null;
  lead_sources: { name: string; name_i18n?: Record<string, string> | null } | null;
  referrers: { name: string } | null;
  converted_client_id: string | null;
};
type Activity = { id: string; kind: string; note: string | null; occurred_at: string; follow_up_at: string | null };
const ACT_KINDS = ['call', 'email', 'meeting', 'note', 'task'] as const;

export function Leads() {
  const { t, i18n } = useTranslation();
  const { currency, timezone } = useOrg();
  const [stages, setStages] = useState<Stage[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<LeadRow | null>(null);
  const [detail, setDetail] = useState<Lead | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [view, setView] = useState(0); // 0 = Kanban, 1 = List

  const load = useCallback(async () => {
    const [st, ld] = await Promise.all([
      supabase.from('pipeline_stages').select('id,name,name_i18n,position,is_won,is_lost').is('deleted_at', null).order('position'),
      supabase.from('leads').select('id,name,contact_name,email,phone,property_type,estimated_value,currency,stage_id,source_id,referrer_id,next_follow_up_at,notes,created_at,converted_client_id,pipeline_stages(name,name_i18n,is_won,is_lost),lead_sources(name,name_i18n),referrers(name)').is('deleted_at', null).order('created_at', { ascending: false }),
    ]);
    setStages((st.data as Stage[]) ?? []);
    setLeads((ld.data as unknown as Lead[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const today = useMemo(() => new Date().toLocaleDateString('en-CA', { timeZone: timezone }), [timezone]);
  const followUps = useMemo(
    () => leads.filter((l) => l.next_follow_up_at && l.next_follow_up_at <= today && !l.pipeline_stages?.is_won && !l.pipeline_stages?.is_lost),
    [leads, today],
  );

  async function moveLead(leadId: string, stageId: string) {
    setLeads((xs) => xs.map((l) => (l.id === leadId ? { ...l, stage_id: stageId } : l)));
    await supabase.from('leads').update({ stage_id: stageId }).eq('id', leadId);
    load();
  }

  const openNew = () => { setEditing(null); setFormOpen(true); };

  return (
    <Layout header={<Topbar actions={<><SegmentedControl items={[t('crm.viewKanban'), t('crm.viewList')]} active={view} onChange={setView} /><Button onClick={openNew}>+ {t('crm.newLead')}</Button></>} />}>
      <div className="flex flex-col gap-[18px] px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.leads')}</h1>

        {followUps.length > 0 && (
          <Card className="p-4">
            <div className="mb-2 flex items-center gap-2">
              <h2 className="text-[15px] font-semibold">{t('crm.followUpToday')}</h2>
              <span className="rounded-pill bg-st-overdue-bg px-2 py-0.5 text-2xs font-medium text-st-overdue-fg">{followUps.length}</span>
            </div>
            <div className="flex flex-col divide-y divide-divider">
              {followUps.map((l) => (
                <button key={l.id} onClick={() => setDetail(l)} className="flex items-center justify-between gap-3 py-2 text-left text-sm hover:bg-bg">
                  <span className="font-medium">{l.name}</span>
                  <span className="flex items-center gap-3 text-muted">
                    <span className="text-xs">{loc(l.pipeline_stages, i18n.language)}</span>
                    <span className="font-mono text-xs text-st-overdue-fg">{fmtDate(l.next_follow_up_at, i18n.language, timezone)}</span>
                  </span>
                </button>
              ))}
            </div>
          </Card>
        )}
      </div>

      {view === 1 && !loading && (
        <div className="px-7 pb-7 pt-[18px]">
          <Card className="overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="text-left text-xs text-muted">
                  <th className="px-5 py-3 font-medium">{t('form.name')}</th>
                  <th className="px-2 py-3 font-medium">{t('crm.stage')}</th>
                  <th className="px-2 py-3 font-medium">{t('crm.source')}</th>
                  <th className="px-2 py-3 font-medium">{t('crm.estimatedValue')}</th>
                  <th className="px-2 py-3 font-medium">{t('crm.followUpDate')}</th>
                </tr></thead>
                <tbody>
                  {leads.length === 0 ? <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                    : leads.map((l) => (
                      <tr key={l.id} onClick={() => setDetail(l)} className="cursor-pointer border-t border-divider hover:bg-bg">
                        <td className="px-5 py-3 font-medium">{l.name}{l.contact_name ? <span className="ml-2 text-xs text-muted">{l.contact_name}</span> : null}</td>
                        <td className="px-2 py-3"><StatusPill tone={l.pipeline_stages?.is_won ? 'done' : l.pipeline_stages?.is_lost ? 'overdue' : 'open'}>{loc(l.pipeline_stages, i18n.language)}</StatusPill></td>
                        <td className="px-2 py-3 text-muted">{l.lead_sources ? loc(l.lead_sources, i18n.language) : '—'}</td>
                        <td className="px-2 py-3 font-mono text-muted">{l.estimated_value != null ? formatCurrency(l.estimated_value, l.currency ?? currency, i18n.language) : '—'}</td>
                        <td className="px-2 py-3 font-mono text-xs text-muted">{l.next_follow_up_at ? fmtDate(l.next_follow_up_at, i18n.language, timezone) : '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {view === 0 && (
      <div className="flex gap-3 overflow-x-auto px-7 pb-7 pt-[18px]">
        {loading ? (
          <div className="p-6 text-sm text-muted">{t('common.loading')}</div>
        ) : (
          stages.map((s) => {
            const col = leads.filter((l) => l.stage_id === s.id);
            const value = col.reduce((sum, l) => sum + Number(l.estimated_value || 0), 0);
            return (
              <div
                key={s.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragId) moveLead(dragId, s.id); setDragId(null); }}
                className="flex w-[260px] shrink-0 flex-col gap-2"
              >
                <div className="flex items-center justify-between px-1">
                  <span className={cn('text-sm font-semibold', s.is_won && 'text-st-done-fg', s.is_lost && 'text-st-overdue-fg')}>{loc(s, i18n.language)}</span>
                  <span className="font-mono text-xs text-muted">{col.length}</span>
                </div>
                <span className="px-1 font-mono text-2xs text-muted">{formatCurrency(value, currency, i18n.language)}</span>
                <div className="flex min-h-[80px] flex-col gap-2 rounded-xl bg-[#F4F5F7] p-2">
                  {col.map((l) => (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={() => setDragId(l.id)}
                      onClick={() => setDetail(l)}
                      className="cursor-pointer rounded-[10px] bg-card p-3 shadow-card"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium leading-tight">{l.name}</span>
                        {l.converted_client_id && <span className="rounded-pill bg-st-done-bg px-1.5 py-0.5 text-2xs text-st-done-fg">{t('crm.converted')}</span>}
                      </div>
                      {l.contact_name && <span className="text-xs text-muted">{l.contact_name}</span>}
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className="font-mono text-2xs text-muted">{l.estimated_value != null ? formatCurrency(l.estimated_value, l.currency ?? currency, i18n.language) : ''}</span>
                        {l.next_follow_up_at && <span className={cn('font-mono text-2xs', l.next_follow_up_at <= today ? 'text-st-overdue-fg' : 'text-muted')}>{fmtDate(l.next_follow_up_at, i18n.language, timezone)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
      )}

      <LeadForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
      {detail && <LeadDetail lead={detail} onClose={() => setDetail(null)} onEdit={() => { setEditing(detail); setDetail(null); setFormOpen(true); }} onChanged={load} />}
    </Layout>
  );
}

function LeadDetail({ lead, onClose, onEdit, onChanged }: { lead: Lead; onClose: () => void; onEdit: () => void; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency, timezone } = useOrg();
  const [acts, setActs] = useState<Activity[]>([]);
  const [na, setNa] = useState({ kind: 'call', note: '', follow_up_at: '' });
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState(false);

  const loadActs = useCallback(async () => {
    const r = await supabase.from('lead_activities').select('id,kind,note,occurred_at,follow_up_at').eq('lead_id', lead.id).is('deleted_at', null).order('occurred_at', { ascending: false });
    setActs((r.data as Activity[]) ?? []);
  }, [lead.id]);
  useEffect(() => { loadActs(); }, [loadActs]);

  async function addActivity(e: React.FormEvent) {
    e.preventDefault();
    if (!na.note.trim() && !na.follow_up_at) return;
    setBusy(true);
    await supabase.from('lead_activities').insert({ organization_id: orgId, lead_id: lead.id, kind: na.kind, note: na.note.trim() || null, follow_up_at: na.follow_up_at || null } as any);
    setBusy(false);
    setNa({ kind: 'call', note: '', follow_up_at: '' });
    loadActs(); onChanged();
  }

  async function convert() {
    setConverting(true);
    const { error } = await supabase.rpc('convert_lead', { p_lead: lead.id });
    setConverting(false);
    if (!error) { onChanged(); onClose(); }
  }

  return (
    <Modal open onClose={onClose} title={lead.name} wide>
      <div className="flex flex-col gap-4 p-5">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          {[
            [t('crm.stage'), loc(lead.pipeline_stages, i18n.language)],
            [t('crm.contactName'), lead.contact_name ?? '—'],
            [t('form.email'), lead.email ?? '—'],
            [t('form.phone'), lead.phone ?? '—'],
            [t('crm.propertyType'), lead.property_type ? t('propertyType.' + lead.property_type) : '—'],
            [t('crm.estimatedValue'), lead.estimated_value != null ? formatCurrency(lead.estimated_value, lead.currency ?? currency, i18n.language) : '—'],
            [t('crm.source'), lead.lead_sources ? loc(lead.lead_sources, i18n.language) : '—'],
            [t('crm.referrer'), lead.referrers?.name ?? '—'],
          ].map(([k, v]) => (
            <div key={k} className="flex flex-col gap-0.5"><span className="text-xs text-muted">{k}</span><span className="font-medium">{v}</span></div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onEdit}>{t('form.edit')}</Button>
          {lead.converted_client_id ? (
            <span className="rounded-pill bg-st-done-bg px-2.5 py-1 text-xs text-st-done-fg">{t('crm.converted')}</span>
          ) : (
            <Button variant="accent" size="sm" disabled={converting} onClick={convert}>{t('crm.convert')}</Button>
          )}
        </div>

        <div className="border-t border-divider pt-3">
          <h3 className="mb-2 text-[15px] font-semibold">{t('crm.activities')}</h3>
          <form onSubmit={addActivity} className="mb-3 flex flex-wrap items-end gap-2">
            <Field label={t('crm.activityKind')}>
              <Select value={na.kind} onChange={(e) => setNa({ ...na, kind: e.target.value })} className="w-32">
                {ACT_KINDS.map((k) => <option key={k} value={k}>{t('crm.actKind.' + k)}</option>)}
              </Select>
            </Field>
            <Field label={t('crm.followUpDate')}><TextInput type="date" value={na.follow_up_at} onChange={(e) => setNa({ ...na, follow_up_at: e.target.value })} className="w-40" /></Field>
            <div className="min-w-[180px] flex-1"><Field label={t('form.notes')}><Textarea value={na.note} onChange={(e) => setNa({ ...na, note: e.target.value })} className="min-h-[38px]" /></Field></div>
            <Button type="submit" size="sm" disabled={busy}>+ {t('cost.add')}</Button>
          </form>
          <div className="flex flex-col divide-y divide-divider">
            {acts.length === 0 && <span className="py-2 text-sm text-muted">{t('common.noData')}</span>}
            {acts.map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-3 py-2 text-sm">
                <div className="flex min-w-0 flex-col">
                  <span className="font-medium">{t('crm.actKind.' + a.kind)}</span>
                  {a.note && <span className="text-muted">{a.note}</span>}
                </div>
                <div className="flex shrink-0 flex-col items-end font-mono text-2xs text-muted">
                  <span>{fmtDate(a.occurred_at, i18n.language, timezone)}</span>
                  {a.follow_up_at && <span className="text-accent">↻ {fmtDate(a.follow_up_at, i18n.language, timezone)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
